var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { FaKey, FaSpinner } from 'react-icons/fa';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { AiTask, aiProviderCanServe, AI_PROVIDER_MODELS } from '@common/contracts';
import { getAiRouting, getApiKeyStatus, getGeneratorMode, getProviderModels, updateAiRouting, updateApiKeys, updateGeneratorMode, updateProviderModels, } from '../../api/settings';
import './ApiKeysModal.scss';
const PROVIDERS = [
    { key: 'anthropic', label: 'Anthropic (Claude)', short: 'Anthropic' },
    { key: 'fal', label: 'fal.ai', short: 'fal.ai' },
    { key: 'openai', label: 'OpenAI', short: 'OpenAI' },
    { key: 'gemini', label: 'Google (Gemini)', short: 'Gemini' },
];
// Rows of the routing matrix, in pipeline order.
const TASKS = [
    { key: AiTask.Text, label: 'Story & obstacles', hint: 'Level text generation' },
    { key: AiTask.Translation, label: 'Translations', hint: 'Publishing translations' },
    { key: AiTask.Vision, label: 'Map recognition', hint: 'Reads an uploaded map (vision)' },
    { key: AiTask.ImageDraft, label: 'Draft map', hint: 'Cheap first pass' },
    { key: AiTask.ImageFinal, label: 'Final map', hint: 'Paid beautify pass' },
];
function errorMessage(err) {
    var _a, _b;
    return ((_b = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) || (err === null || err === void 0 ? void 0 : err.message) || 'Failed to update keys';
}
function ApiKeysModal({ isOpen, onClose }) {
    const [status, setStatus] = useState(null);
    const [routing, setRouting] = useState(null);
    const [genMode, setGenMode] = useState(null);
    const [models, setModels] = useState(null);
    const [busyModel, setBusyModel] = useState(null);
    // Providers whose current model is not in the curated list edit it as free text.
    const [customModel, setCustomModel] = useState({});
    const [inputs, setInputs] = useState({ anthropic: '', fal: '', openai: '', gemini: '' });
    const [loading, setLoading] = useState(false);
    // Which provider row is currently saving/clearing (one row at a time).
    const [busyKey, setBusyKey] = useState(null);
    const [busyTask, setBusyTask] = useState(null);
    const [busyMode, setBusyMode] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => {
        if (!isOpen)
            return;
        setInputs({ anthropic: '', fal: '', openai: '', gemini: '' });
        setError(null);
        setBusyKey(null);
        setBusyTask(null);
        setLoading(true);
        setCustomModel({});
        Promise.all([getApiKeyStatus(), getAiRouting(), getGeneratorMode(), getProviderModels()])
            .then(([keys, route, mode, picks]) => { setStatus(keys); setRouting(route); setGenMode(mode); setModels(picks); })
            .catch((err) => setError(errorMessage(err)))
            .finally(() => setLoading(false));
    }, [isOpen]);
    const pickModel = (provider, modelId) => __awaiter(this, void 0, void 0, function* () {
        if (!models || models[provider] === modelId || !modelId.trim())
            return;
        const previous = models;
        setModels(Object.assign(Object.assign({}, models), { [provider]: modelId }));
        setBusyModel(provider);
        setError(null);
        try {
            setModels(yield updateProviderModels({ [provider]: modelId }));
        }
        catch (err) {
            setModels(previous);
            setError(errorMessage(err));
        }
        finally {
            setBusyModel(null);
        }
    });
    const switchGenMode = (mode) => __awaiter(this, void 0, void 0, function* () {
        if (genMode === mode || busyMode)
            return;
        const prev = genMode;
        setGenMode(mode);
        setBusyMode(true);
        setError(null);
        try {
            setGenMode(yield updateGeneratorMode(mode));
        }
        catch (err) {
            setGenMode(prev);
            setError(errorMessage(err));
        }
        finally {
            setBusyMode(false);
        }
    });
    // Freeze the page behind the modal so only the modal scrolls.
    useBodyScrollLock(isOpen);
    if (!isOpen)
        return null;
    const saveKey = (provider) => __awaiter(this, void 0, void 0, function* () {
        const value = inputs[provider].trim();
        if (!value)
            return;
        setBusyKey(provider);
        setError(null);
        try {
            const next = yield updateApiKeys({ [provider]: value });
            setStatus(next);
            setInputs((prev) => (Object.assign(Object.assign({}, prev), { [provider]: '' })));
        }
        catch (err) {
            setError(errorMessage(err));
        }
        finally {
            setBusyKey(null);
        }
    });
    const clearKey = (provider) => __awaiter(this, void 0, void 0, function* () {
        setBusyKey(provider);
        setError(null);
        try {
            const next = yield updateApiKeys({ [provider]: null });
            setStatus(next);
        }
        catch (err) {
            setError(errorMessage(err));
        }
        finally {
            setBusyKey(null);
        }
    });
    // Routing is optimistic: the pick lands instantly, and a failed PATCH rolls the
    // row back to what the server actually has.
    const routeTask = (task, provider) => __awaiter(this, void 0, void 0, function* () {
        if (!routing || routing[task] === provider)
            return;
        const previous = routing;
        setRouting(Object.assign(Object.assign({}, routing), { [task]: provider }));
        setBusyTask(task);
        setError(null);
        try {
            setRouting(yield updateAiRouting({ [task]: provider }));
        }
        catch (err) {
            setRouting(previous);
            setError(errorMessage(err));
        }
        finally {
            setBusyTask(null);
        }
    });
    return (_jsx("div", { className: "modal-overlay", children: _jsxs("div", { className: "modal-content api-keys-modal", children: [_jsxs("div", { className: "ak-head", children: [_jsxs("h2", { children: [_jsx(FaKey, {}), " AI provider keys"] }), _jsx("button", { className: "ak-close", onClick: onClose, disabled: !!busyKey, "aria-label": "Close", children: "\u00D7" })] }), error && _jsx("p", { className: "ak-error", children: error }), loading ? (_jsxs("p", { className: "ak-loading", children: [_jsx(FaSpinner, { className: "ak-spin" }), " Loading\u2026"] })) : (_jsx("div", { className: "ak-rows", children: PROVIDERS.map(({ key, label }) => {
                        const state = status === null || status === void 0 ? void 0 : status[key];
                        const isSet = !!(state === null || state === void 0 ? void 0 : state.set);
                        const busy = busyKey === key;
                        const value = inputs[key];
                        // What this provider currently runs — derived from the matrix
                        // below, not hard-coded, so the two never disagree.
                        const jobs = TASKS.filter(t => (routing === null || routing === void 0 ? void 0 : routing[t.key]) === key).map(t => t.label);
                        return (_jsxs("div", { className: "ak-row", children: [_jsxs("div", { className: "ak-row-top", children: [_jsx("span", { className: `ak-dot ${isSet ? 'on' : 'off'}`, "aria-hidden": "true" }), _jsx("span", { className: "ak-label", children: label }), _jsx("span", { className: "ak-purpose", children: jobs.length ? jobs.join(' · ') : 'Unused' }), _jsx("span", { className: `ak-state ${isSet ? 'on' : 'off'}`, children: isSet ? `Configured · ${state === null || state === void 0 ? void 0 : state.hint}` : 'Not set' })] }), _jsxs("div", { className: "ak-controls", children: [_jsx("input", { type: "password", value: value, onChange: (e) => setInputs((prev) => (Object.assign(Object.assign({}, prev), { [key]: e.target.value }))), placeholder: isSet ? 'Enter a new key to replace' : 'Paste the key', disabled: busy, autoComplete: "off", onKeyDown: (e) => { if (e.key === 'Enter')
                                                saveKey(key); } }), _jsx("button", { className: "ak-save", onClick: () => saveKey(key), disabled: busy || !value.trim(), children: busy ? _jsx(FaSpinner, { className: "ak-spin" }) : 'Save' }), isSet && (_jsx("button", { className: "ak-clear", onClick: () => clearKey(key), disabled: busy, "aria-label": `Clear ${label} key`, children: "Clear" }))] }), models && (() => {
                                    var _a;
                                    const provider = key;
                                    const known = AI_PROVIDER_MODELS[provider];
                                    const current = models[provider];
                                    const editing = customModel[provider];
                                    const isCustom = editing !== undefined || !known.some(m => m.id === current);
                                    const modelBusy = busyModel === provider;
                                    const hint = (_a = known.find(m => m.id === current)) === null || _a === void 0 ? void 0 : _a.hint;
                                    return (_jsxs("div", { className: "ak-model", children: [_jsx("span", { className: "ak-model-label", children: "Model" }), _jsxs("select", { value: isCustom ? '__custom' : current, disabled: modelBusy, onChange: (e) => {
                                                    const v = e.target.value;
                                                    if (v === '__custom') {
                                                        setCustomModel(p => (Object.assign(Object.assign({}, p), { [provider]: current })));
                                                        return;
                                                    }
                                                    setCustomModel(p => { const n = Object.assign({}, p); delete n[provider]; return n; });
                                                    pickModel(provider, v);
                                                }, children: [known.map(m => (_jsxs("option", { value: m.id, children: [m.label, m.hint ? ` — ${m.hint}` : ''] }, m.id))), _jsx("option", { value: "__custom", children: "Custom\u2026" })] }), isCustom && (_jsxs(_Fragment, { children: [_jsx("input", { className: "ak-model-custom", type: "text", value: editing !== null && editing !== void 0 ? editing : current, placeholder: "model id", disabled: modelBusy, onChange: (e) => setCustomModel(p => (Object.assign(Object.assign({}, p), { [provider]: e.target.value }))), onKeyDown: (e) => { if (e.key === 'Enter')
                                                            pickModel(provider, (editing !== null && editing !== void 0 ? editing : current).trim()); } }), _jsx("button", { className: "ak-save", disabled: modelBusy || !(editing !== null && editing !== void 0 ? editing : current).trim() || (editing !== null && editing !== void 0 ? editing : current).trim() === current, onClick: () => pickModel(provider, (editing !== null && editing !== void 0 ? editing : current).trim()), children: modelBusy ? _jsx(FaSpinner, { className: "ak-spin" }) : 'Apply' })] })), !isCustom && hint && _jsx("span", { className: "ak-model-hint", children: hint }), modelBusy && !isCustom && _jsx(FaSpinner, { className: "ak-spin" })] }));
                                })()] }, key));
                    }) })), !loading && routing && (_jsxs("div", { className: "ak-matrix", children: [_jsxs("div", { className: "ak-matrix-head", children: [_jsx("h3", { children: "Task routing" }), _jsx("p", { children: "Pick which provider runs each job." })] }), _jsxs("table", { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { scope: "col", className: "ak-task-col", children: "Job" }), PROVIDERS.map(p => (_jsx("th", { scope: "col", children: p.short }, p.key)))] }) }), _jsx("tbody", { children: TASKS.map(task => {
                                        var _a;
                                        const chosen = routing[task.key];
                                        const missingKey = !((_a = status === null || status === void 0 ? void 0 : status[chosen]) === null || _a === void 0 ? void 0 : _a.set);
                                        return (_jsxs("tr", { className: busyTask === task.key ? 'busy' : undefined, children: [_jsxs("th", { scope: "row", className: "ak-task-col", children: [_jsx("span", { className: "ak-task-label", children: task.label }), _jsx("span", { className: "ak-task-hint", children: missingKey ? 'Needs an API key' : task.hint })] }), PROVIDERS.map(p => {
                                                    var _a;
                                                    // A provider that can't do the job at all gets a dash, not a
                                                    // disabled radio — there is nothing to choose.
                                                    if (!aiProviderCanServe(task.key, p.key)) {
                                                        return _jsx("td", { className: "ak-na", "aria-label": "Not supported", children: "\u2014" }, p.key);
                                                    }
                                                    const keyless = !((_a = status === null || status === void 0 ? void 0 : status[p.key]) === null || _a === void 0 ? void 0 : _a.set);
                                                    return (_jsx("td", { className: keyless ? 'ak-keyless' : undefined, children: _jsxs("label", { children: [_jsx("input", { type: "radio", name: `route-${task.key}`, checked: chosen === p.key, disabled: !!busyTask, onChange: () => routeTask(task.key, p.key) }), _jsx("span", { className: "ak-sr", children: `${p.short} for ${task.label}` })] }) }, p.key));
                                                })] }, task.key));
                                    }) })] })] })), !loading && genMode && (_jsxs("div", { className: "ak-genmode", children: [_jsxs("div", { className: "ak-matrix-head", children: [_jsx("h3", { children: "Generation flow" }), _jsx("p", { children: "How creators build a level's layout. Switch to Legacy to roll back to the original walls generator." })] }), _jsxs("div", { className: "ak-genmode-toggle", role: "group", "aria-label": "Generation flow", children: [_jsx("button", { type: "button", className: genMode === 'route' ? 'on' : '', disabled: busyMode, onClick: () => switchGenMode('route'), children: "Route (draw the path)" }), _jsx("button", { type: "button", className: genMode === 'legacy' ? 'on' : '', disabled: busyMode, onClick: () => switchGenMode('legacy'), children: "Legacy (paint walls)" })] })] })), _jsx("div", { className: "modal-actions", children: _jsx("button", { type: "button", onClick: onClose, disabled: !!busyKey, children: "Close" }) })] }) }));
}
export default ApiKeysModal;
